import { styled } from 'styled-components';

import { Col, Container, Image, Row } from 'react-bootstrap';
import {
  InvertedSpecialtyMap,
  SpecialtyMap,
} from 'vivaa-api/dist/models/common';
import { api } from 'common';
import { Link, match } from 'react-router-dom';

import { useSelector } from 'react-redux';
import * as store from '../../../../reducers/store';

import { EmbeddedFluidContainer, StyledTypography } from '../Common';

import PrimaryCare from '../../../../assets/primarycare.png';

import { Card, HStack, Typography, VStack } from '@vivaahealth/design-system';

import { ReactComponent as LeftArrow } from '../../../../assets/left_arrow.svg';

import Specialty = api.Specialty;

const COMMON_SPECIALTIES = [
  SpecialtyMap[Specialty.AllergyAndImmunology],
  SpecialtyMap[Specialty.Cardiology],
  SpecialtyMap[Specialty.Dermatology],
  SpecialtyMap[Specialty.Endocrinology],
  SpecialtyMap[Specialty.Gastroenterology],
  SpecialtyMap[Specialty.Psychiatry],
  SpecialtyMap[Specialty.RadiationOncology],
  SpecialtyMap[Specialty.Orthopedics],
  SpecialtyMap[Specialty.Obgyn],
];

interface Props {
  match: match;
}

const Specialties: React.FC<Props> = ({ match }) => {
  const embedded = useSelector((store: store.All) => store.embedded);

  const heading = (
    <>
      <Row className="mt-3 mb-2 justify-content-between me-0">
        <Col xs="auto">
          <Typography variant="headline-large" bold>
            Common Specialties
          </Typography>
        </Col>
        {embedded && (
          <Col xs="auto" className="d-table pe-0">
            <StyledLink
              to={{
                pathname: `${match.url}/search`,
              }}
              className="d-table-cell align-middle"
            >
              <StyledTypography
                variant="paragraph-large"
                color="var(--color-primary)"
                bold
              >
                Search all providers
              </StyledTypography>
            </StyledLink>
          </Col>
        )}
      </Row>
    </>
  );

  const primaryCareCard = (
    <Card>
      <Card.Body>
        <VStack gap={16} paddingY={20}>
          <Row>
            <Col className="d-flex" xs={12} md={4} lg={3}>
              <SpotlightImage
                fluid
                src={String(PrimaryCare)}
                alt="primary care"
              />
            </Col>
            <Col xs={12} md={8} lg={9}>
              <div className="mb-2">
                <Typography variant="headline-small" bold>
                  Primary Care
                </Typography>
              </div>
              <div className="mb-1">
                <Typography variant="paragraph-large">
                  PCMA Primary Care provides a wide range of services to
                  patients of all ages. We offer several convenient locations
                  and coordinated care from PCMA specialists.
                </Typography>
              </div>
              <StyledLink
                color="var(--color-primary)"
                to={`${match.url}/search?specialty=${Specialty.PrimaryCare}`}
                className="mb-3"
              >
                <Row>
                  <Col xs="auto" className="pe-0">
                    <StyledTypography
                      variant="paragraph-large"
                      color="var(--color-primary)"
                      bold
                    >
                      Find a primary care provider
                    </StyledTypography>
                  </Col>
                  <Col className="ps-2 pt-1">
                    <LeftArrow transform="rotate(180, 0, 0)" />
                  </Col>
                </Row>
              </StyledLink>
            </Col>
          </Row>
        </VStack>
      </Card.Body>
    </Card>
  );

  const specialties = (
    <StyledRow xs={1} md={3} className="mt-0">
      {COMMON_SPECIALTIES.map((specialty, idx) => (
        <Col key={specialty}>
          <StyledLink
            to={`${match.url}/search?specialty=${InvertedSpecialtyMap[specialty]}`}
          >
            <Card>
              <Card.Header>
                <HStack
                  alignItems="center"
                  justifyContent="space-between"
                  paddingRight={8}
                  width="100%"
                >
                  <Typography variant="paragraph-large">{specialty}</Typography>
                  <LeftArrow transform="rotate(180, 0, 0)" />
                </HStack>
              </Card.Header>
            </Card>
          </StyledLink>
        </Col>
      ))}
    </StyledRow>
  );

  return embedded ? (
    <>
      <EmbeddedFluidContainer fluid className="mt-1">
        {heading}
      </EmbeddedFluidContainer>
      <EmbeddedFluidContainer fluid className="mb-3">
        {primaryCareCard}
      </EmbeddedFluidContainer>
      <EmbeddedFluidContainer fluid className="mt-3">
        {specialties}
      </EmbeddedFluidContainer>
    </>
  ) : (
    <>
      <LimitedContainer className="text-left mt-4">{heading}</LimitedContainer>
      <LimitedContainer>{primaryCareCard}</LimitedContainer>
      <LimitedContainer className="mt-3">{specialties}</LimitedContainer>
    </>
  );
};
export default Specialties;

const LimitedContainer = styled(Container)`
  @media (min-width: 1200px) {
    max-width: 1032px;
  }
`;

const SpotlightImage = styled(Image)`
  margin: auto;
  @media (min-width: 0px) {
    display: none;
  }
  @media (min-width: 768px) {
    display: block;
    max-width: 191px;
    max-height: 118.48px;
    width: auto;
    height: auto;
  }
`;

const StyledLink = styled(Link).attrs<{
  color: string;
  textDecoration: string;
}>((props) => ({
  color: props.color || 'var(--color-black)',
  textDecoration: props.textDecoration || 'none',
}))`
  color: ${(props) => props.color};
  text-decoration-line: ${(props) => props.textDecoration};
`;

const StyledRow = styled(Row)`
  --bs-gutter-y: 12px;
  --bs-gutter-x: 12px;
`;
